import { Link } from "react-router-dom";
import { GitBranch, GraduationCap, Users, Search } from "lucide-react";
import CoachTreeGraph from "./CoachTreeGraph";

/**
 * COACHING TREE panel — shows a coach's inferred mentors + disciples.
 * Both directions of credit: who they learned under, and who they developed.
 * Data comes from scripts/build_coaching_tree.py via GET /coaches/{id}
 * (mentors_inferred[] + disciples[]).
 */
export default function CoachingTreePanel({ mentorsInferred = [], disciples = [], variant = "detail", coachId = null, coachName = "" }) {
  const hasMentors = Array.isArray(mentorsInferred) && mentorsInferred.length > 0;
  const hasDisciples = Array.isArray(disciples) && disciples.length > 0;
  if (!hasMentors && !hasDisciples) return null;

  const tierColor = (t) => {
    const norm = (t || "").toLowerCase();
    if (norm === "elite") return "text-blaze border-blaze";
    if (norm === "national") return "text-volt border-volt";
    if (norm === "regional") return "text-neutral-100 border-neutral-500";
    return "text-neutral-300 border-neutral-700";
  };

  return (
    <div className="bg-appcard border border-neutral-800 p-6" data-testid="coaching-tree-panel">
      <div className="flex items-center gap-3 mb-4">
        <GitBranch className="text-blaze" size={22} strokeWidth={1.75} />
        <div>
          <div className="text-[10px] tracking-widest text-blaze font-bold">THE COACHING TREE</div>
          <div className="heading text-2xl mt-1">
            {variant === "self" ? "YOUR TREE" : "WHO SHAPED THEM · WHO THEY SHAPED"}
          </div>
        </div>
      </div>

      <div className="mt-2 mb-6 border border-neutral-800 bg-[#0B0B0B]">
        <CoachTreeGraph coachName={coachName} mentors={mentorsInferred} disciples={disciples} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* LEARNED UNDER */}
        <div data-testid="tree-mentors">
          <div className="flex items-center gap-2 pb-3 border-b border-neutral-800">
            <GraduationCap className="text-volt" size={16} />
            <div className="text-[10px] tracking-widest text-volt font-bold">LEARNED UNDER</div>
            <span className="ml-auto text-mono text-[10px] text-neutral-400">
              {hasMentors ? mentorsInferred.length : 0}
            </span>
          </div>
          {hasMentors ? (
            <ul className="mt-3 space-y-2">
              {mentorsInferred.slice(0, 8).map((m, i) => (
                <li key={`${m.coach_profile_id || m.name || "m"}-${i}`} className="flex items-start gap-2">
                  <div className={`shrink-0 w-1 h-full min-h-[24px] ${tierColor(m.tier).replace('text-','bg-').replace('border-','bg-')}`} />
                  <div className="min-w-0">
                    {m.coach_profile_id ? (
                      <Link
                        to={`/coaches/${m.coach_profile_id}`}
                        className="text-white hover:text-volt text-sm font-medium truncate block"
                        data-testid={`mentor-link-${i}`}
                      >
                        {m.name || "Coach"}
                      </Link>
                    ) : (
                      <div className="text-white text-sm font-medium truncate">{m.name || "Coach"}</div>
                    )}
                    <div className="text-[10px] text-neutral-400 tracking-widest">
                      {m.organization ? `${m.organization}` : ""}
                      {m.level ? ` · ${m.level}` : ""}
                      {m.years_under ? ` · ${m.years_under} yr${m.years_under > 1 ? "s" : ""}` : ""}
                    </div>
                    {(m.tier || m.hof || m.national_champion || m.championships > 0) && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {m.tier && (
                          <span className={`text-[9px] tracking-widest px-1.5 py-0.5 border ${tierColor(m.tier)}`}>
                            {(m.tier || "").toUpperCase()}
                          </span>
                        )}
                        {m.hof && (
                          <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-blaze text-blaze">HOF</span>
                        )}
                        {m.national_champion && (
                          <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-blaze text-blaze">NAT CHAMP</span>
                        )}
                        {m.championships > 0 && !m.national_champion && (
                          <span className="text-[9px] tracking-widest px-1.5 py-0.5 border border-neutral-600 text-neutral-300">
                            {m.championships} TITLE{m.championships > 1 ? "S" : ""}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              ))}
              {mentorsInferred.length > 8 && (
                <li className="text-[10px] tracking-widest text-neutral-500 pt-1">
                  + {mentorsInferred.length - 8} more mentors
                </li>
              )}
            </ul>
          ) : (
            <p className="mt-3 text-xs text-neutral-500">No inferred mentors yet.</p>
          )}
        </div>

        {/* COACHES DEVELOPED */}
        <div data-testid="tree-disciples">
          <div className="flex items-center gap-2 pb-3 border-b border-neutral-800">
            <Users className="text-blaze" size={16} />
            <div className="text-[10px] tracking-widest text-blaze font-bold">COACHES DEVELOPED</div>
            <span className="ml-auto text-mono text-[10px] text-neutral-400">
              {hasDisciples ? disciples.length : 0}
            </span>
          </div>
          {hasDisciples ? (
            <ul className="mt-3 space-y-2">
              {disciples.slice(0, 8).map((d, i) => (
                <li key={`${d.coach_profile_id || d.name || "d"}-${i}`} className="flex items-start gap-2">
                  <div className="shrink-0 w-1 min-h-[24px] bg-blaze" />
                  <div className="min-w-0">
                    {d.coach_profile_id ? (
                      <Link
                        to={`/coaches/${d.coach_profile_id}`}
                        className="text-white hover:text-blaze text-sm font-medium truncate block"
                        data-testid={`disciple-link-${i}`}
                      >
                        {d.name || "Coach"}
                      </Link>
                    ) : (
                      <div className="text-white text-sm font-medium truncate">{d.name || "Coach"}</div>
                    )}
                    <div className="text-[10px] text-neutral-400 tracking-widest">
                      {d.as_role ? `${d.as_role}` : ""}
                      {d.organization ? ` @ ${d.organization}` : ""}
                      {d.years ? ` · ${d.years}` : ""}
                    </div>
                  </div>
                </li>
              ))}
              {disciples.length > 8 && (
                <li className="text-[10px] tracking-widest text-neutral-500 pt-1">
                  + {disciples.length - 8} more coaches developed
                </li>
              )}
            </ul>
          ) : (
            <p className="mt-3 text-xs text-neutral-500">
              {variant === "self"
                ? "As you take on assistants and grad-assistants, they'll appear here."
                : "No documented disciples yet."}
            </p>
          )}
          {hasDisciples && coachId && variant === "detail" && (
            <Link
              to={`/ad/talent?learned_under=${coachId}`}
              className="mt-4 inline-flex items-center gap-2 border border-blaze text-blaze hover:bg-blaze hover:text-white text-[10px] tracking-widest px-3 py-1.5 font-bold transition-colors"
              data-testid="show-tree-in-pool"
            >
              <Search size={12} />
              SHOW ALL COACHES WHO LEARNED UNDER {(coachName || "THIS COACH").toUpperCase()}
            </Link>
          )}
        </div>
      </div>

      <p className="text-[10px] tracking-widest text-neutral-500 mt-5 leading-relaxed">
        Inferred from public coaching-history overlap. Elite / HOF / national-champion mentors boost your CIR
        via the Mentor Tree component (up to 10 pts).
      </p>
    </div>
  );
}
